import React, { useEffect, useState } from "react";
import {
  AttachmentSection,
  Button,
  Chip,
  DocumentsList,
  FormFieldSet,
  Modal,
  PageHeader,
  PageTitle,
  showToast,
  useFormHandler,
} from "../../../../../../../components";
import {
  documentOptions,
  invoiceOptions,
} from "../../../../../../../assets/data/projectData";
import { RecordStatus } from "../../../../../../../assets/data/enumData";
import { v4 as uuidv4 } from "uuid";
import { filterEmptyValues } from "../../../../../../../utils/filterEmptyValues";
import { addDocumentStart } from "../../../../../../../redux/slices/universal/documentsSlice";
import { uploadStart } from "../../../../../../../redux/slices/universal/fileUploadSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  addCurrentPlacement,
  addNewPlacementStart,
} from "../../../../../../../redux/slices/placements/placementsSlice";
import { useLoginUser } from "../../../../../../../hooks/useLoginUser";
import { transformFiles } from "../../../../../../../utils/transformFiles";
import axiosRequest from "../../../../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../../../../redux/apiConfig";

const InvoiceDetails = ({ setActiveTab, currentPlacement, editData }) => {
  const { employeeProfileUiid, currentEmployee } = useSelector(
    (state) => state.employee,
  );
  const isEditable = !!editData;
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [fetchPerticularData, setFetchPerticularData] = useState(null);
  const dispatch = useDispatch();
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const {
    formData,
    errors,
    handleChange,
    validateAllFields,
    setFormData,
    setErrors,
  } = useFormHandler(
    {
      invoicePeriod: "",
      invoiceDate: "",
      includePONumber: "no",
      includeAggreementAttachment: "no",
      fundingNonFunding: "yes",
      additionalInfo: "",
      ccEmail: [],
      // documentRows: [
      attachments: [
        {
          id: uuidv4(),
          attachmentName: "",
          attachmentComments: "",
          documentType: "",
          documentStatus: "",
          chooseFiles: [],
        },
      ],
    },
    {
      invoicePeriod: { type: "text", required: true },
      invoiceDate: { type: "date", required: true },
      // documentRows: [
      attachments: [
        {
          // attachmentName: { type: "text", required: true },
          documentType: { type: "text" },
          documentStatus: { type: "select" },
          chooseFiles: {
            type: "file",
            // required: true,
            // allowedFiles: [
            //   { ext: "png", mime: "image/png" },
            //   { ext: "txt", mime: "text/plain" },
            // ],
          },
        },
      ],
    },
  );

  const handleAddRow = () => {
    // Store current errors
    const currentErrors = { ...errors };

    // Update formData directly instead of using handleChange
    setFormData({
      ...formData,
      documentRows: [
        ...formData.documentRows,
        {
          id: uuidv4(),
          documentName: "",
          comments: "",
          documentType: "",
          active: "",
          chooseFiles: [],
        },
      ],
    });

    // Make sure to preserve errors
    setErrors(currentErrors);
  };

  const handleRemoveRow = (id) => {
    if (formData.documentRows.length > 1) {
      // Find the index of the row to be removed
      const indexToRemove = formData.documentRows.findIndex(
        (row) => row.id === id,
      );

      // Update formData directly
      const newDocumentRows = formData.documentRows.filter(
        (row) => row.id !== id,
      );

      setFormData({
        ...formData,
        documentRows: newDocumentRows,
      });

      // Update the errors state to remove errors for the deleted row
      if (errors?.documentRows) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          if (newErrors.documentRows) {
            // Remove the errors for the removed index
            const updatedRowErrors = [...newErrors.documentRows];
            updatedRowErrors.splice(indexToRemove, 1);
            newErrors.documentRows = updatedRowErrors;
          }

          return newErrors;
        });
      }
    }
  };

  const getAttachmentError = (index, field) => {
    return errors?.attachments?.[index]?.[field] || "";
  };

  const handleFieldChange = (index, field, value) => {
    handleChange(`documentRows[${index}].${field}`, value);
  };

  const handleSave = () => {
    const isValid = validateAllFields();
    if (isValid) {
      const invoices = {
        period: formData?.invoicePeriod,
        invoiceDate: formData?.invoiceDate,
        includePONum: formData?.includePONumber ? true : false,
        additionalInfo: formData?.additionalInfo,
        cc: formData?.ccEmail || [],
      };
      // Filter valid document entries
      const cleanAttachments = formData.attachments.filter(
        (att) =>
          att.attachmentName.trim() !== "" ||
          att.documentType.trim() !== "" ||
          (att.chooseFiles && att.chooseFiles.length > 0),
      );

      // Map files with their parent doc id
      const cleanAttachmentsFiles = cleanAttachments.flatMap((doc) =>
        doc.chooseFiles.map((file) => {
          file.id = doc.id;
          return file;
        }),
      );
      const submissionData = filterEmptyValues(invoices);
      const uiid = fetchPerticularData?.uiid ?? "";
      dispatch(
        addNewPlacementStart({
          uiid: uiid,
          rsd: submissionData,
          rsn: "permanetPlacementInvoiceDeatils",
          payload: [
            {
              type: "SET",
              value: {
                invoices: {
                  ...submissionData,
                },
              },
            },
          ],
          onSuccess: async (payload) => {
            if (cleanAttachmentsFiles.length > 0) {
              dispatch(
                uploadStart({
                  uploadType: "multiple",
                  field: "doc",
                  uiid: uiid,
                  entity: "placements",
                  category: "Documents",
                  coll: "Placement",
                  doc: cleanAttachmentsFiles,
                  onSuccess: (fileData) => {
                    const attachmentsPayload = cleanAttachments.map((clean) => {
                      return {
                        addedBy: loginUserUiid,
                        docType: clean.documentType,
                        parent: { employee: employeeProfileUiid },
                        ref: {
                          id: currentPlacement?.uiid,
                          entity: "Placement",
                          path: "invoices.documents",
                        },
                        file: (clean.chooseFiles || []).map((file) => {
                          const matchedFile = fileData.find(
                            (f) => f?.fileRefId === clean.id && f.key,
                          );
                          return {
                            name: clean.attachmentName,
                            comments: clean.attachmentComments,
                            status: clean.documentStatus,
                            keys: matchedFile ? [matchedFile.key] : [],
                          };
                        }),
                      };
                    });
                    dispatch(
                      addDocumentStart({
                        coll: "Document",
                        refColl: "Placement",
                        refId: uiid,
                        refField: "invoices.documents",
                        refPatchId: "",
                        payload: {
                          addedBy: loginUserUiid,
                          attachments: attachmentsPayload,
                        },
                        onSuccess: async () => {
                          setActiveTab((prev) => prev + 1);
                          dispatch(
                            addCurrentPlacement({
                              invoicesDocuments: attachmentsPayload,
                            }),
                          );
                          // handleModalClose(); // Optional: uncomment if you want to close the modal on success
                        },
                      }),
                    );
                  },
                }),
              );
            } else {
              setActiveTab((prev) => prev + 1);
            }
          },
        }),
      );
    } else {
      showToast("Please fill all fields", "error");
    }
  };
  //   useEffect(() => {
  //     const permanetPlacementInvoiceDeatils =
  //       currentPlacement?.permanetPlacementInvoiceDeatils;
  //     if (permanetPlacementInvoiceDeatils) {
  //       handleChange("invoicePeriod", permanetPlacementInvoiceDeatils?.period);
  //       handleChange("invoiceDate", permanetPlacementInvoiceDeatils?.invoiceDate);
  //       handleChange(
  //         "includePONumber",
  //         permanetPlacementInvoiceDeatils?.includePONum === true ? "yes" : "no"
  //       );
  //     }
  //   }, [currentPlacement]);
  //  useEffect(()=>{
  //       const permanetPlacementInvoiceDeatils=currentPlacement?.permanetPlacementInvoiceDeatils||{}
  //      const isEmptyOrMissing =
  //       !permanetPlacementInvoiceDeatils ||
  //       Object.keys(permanetPlacementInvoiceDeatils).length === 0;
  //   const invoices=editData?.invoices||{};
  //     if (isEmptyOrMissing && isEditable) {
  //      handleChange("invoicePeriod",invoices?.period)
  //      handleChange("invoiceDate",invoices?.invoiceDate)
  //      handleChange("includePONumber",invoices?.includePONum===false?"no":invoices?.includePONum===true?"yes":"")
  //      handleChange("additionalInfo", invoices?.additionalInfo)
  //      handleChange("ccEmail", invoices?.cc||[])
  //     }
  //     },[isEditable])
  const combineAttachments = fetchPerticularData
    ? transformFiles(fetchPerticularData?.invoices?.documents || [])
    : [];

  useEffect(() => {
    const fetchData = async () => {
      const uiid =
        currentPlacement?.uiid ??
        editData?.uiid ??
        currentPlacement?.data?.doc?.uiid ??
        "";
      if (!uiid) return;
      const filter = {
        uiid: { type: "NONE", value: uiid },
      };
      try {
        const response = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.PLACEMENTS.GET_BY_UIID,
          params: {
            coll: "Placement",
            fil: JSON.stringify(filter),
            ret: "invoices uiid",
            ref: "InvDoc",
            // incl: "uiid businessName",
          },
        });

        const data = response?.data?.data;
        if (data) {
          const invoices = data?.invoices || {};
          setFetchPerticularData(data);
          handleChange("invoicePeriod", invoices?.period);
          handleChange("invoiceDate", invoices?.invoiceDate);
          handleChange(
            "includePONumber",
            invoices?.includePONum === false
              ? "no"
              : invoices?.includePONum === true
                ? "yes"
                : "",
          );
          handleChange("additionalInfo", invoices?.additionalInfo);
          handleChange("ccEmail", invoices?.cc || []);
        }
      } catch (error) {
        setFetchPerticularData(null);
      }
    };

    fetchData();
  }, [currentPlacement, isEditable]);

  return (
    // <div className="px-8 py-5 overflow-auto min-h-[65vh]">
    <div className="px-8 py-5 overflow-auto">
      <div className="grid grid-cols-2 lg:grid-cols-3  gap-x-8 gap-y-6 max-w-7xl">
        <div>
          <FormFieldSet
            type="select"
            title="Invoice Period"
            placeholder="Enter Invoice Period"
            value={formData.invoicePeriod}
            onChange={(value) => handleChange("invoicePeriod", value)}
            error={errors.invoicePeriod}
            options={invoiceOptions}
          />
        </div>
        <div>
          <FormFieldSet
            type="datepicker"
            title="Invoice Date"
            placeholder="Select Invoice Date"
            value={formData.invoiceDate}
            onChange={(value) => handleChange("invoiceDate", value)}
            error={errors.invoiceDate}
            showDropdown={false}
          />
        </div>
        <div>
          <FormFieldSet
            type="radio"
            title="Include PO Number"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
            value={formData.includePONumber}
            onChange={(event) =>
              handleChange("includePONumber", event.target.value)
            }
            error={errors.includePONumber}
            name="include po Number"
          />
        </div>
        <div>
          <FormFieldSet
            type="radio"
            title="Include Aggreement Attachment"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
            value={formData.includeAggreementAttachment}
            onChange={(event) =>
              handleChange("includeAggreementAttachment", event.target.value)
            }
            error={errors.includeAggreementAttachment}
            name="Include Aggreement Attachment"
          />
        </div>
        <div>
          <FormFieldSet
            type="radio"
            title="Funding / Non Funding"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
            value={formData.fundingNonFunding}
            onChange={(event) =>
              handleChange("fundingNonFunding", event.target.value)
            }
            error={errors.fundingNonFunding}
            name="Funding / Non Funding"
          />
        </div>
        <div></div>
        {formData?.fundingNonFunding === "yes" && (
          <>
            <div>
              <FormFieldSet
                type="textArea"
                title={"Additional Info"}
                placeholder="Enter"
                value={formData.additionalInfo}
                onChange={(value) => handleChange("additionalInfo", value)}
                error={errors.additionalInfo}
              />
            </div>

            <FormFieldSet
              // type="text"
              type="email"
              title={"CC Email ID:-(Please type and enter)"}
              placeholder="Enter CC Email ID"
              value={formData.ccEmail}
              onChange={(value) => handleChange("ccEmail", value)}
              multiple
              error={errors.ccEmail}
            />
          </>
        )}
      </div>

      <div className="mt-6 mb-0">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle title="Documents" tag="h6" />
            </div>
          }
        />
      </div>
      <div>
        {/* {formData.documentRows.map((row, index) => (
          <div key={row.id} className="flex gap-4 items-start mt-0">
            <div className="flex-1">
              <FormFieldSet
                type="text"
                title=""
                placeholder="Document Name"
                value={row.documentName}
                onChange={(value) =>
                  handleFieldChange(index, "documentName", value)
                }
                error={getAttachmentError(index, "documentName")}
              />
            </div>
            <div className="flex-1">
              <FormFieldSet
                type="text"
                title=""
                placeholder="Comments"
                value={row.comments}
                onChange={(value) =>
                  handleFieldChange(index, "comments", value)
                }
                error={getAttachmentError(index, "comments")}
              />
            </div>
            <div className="flex-1">
              <FormFieldSet
                type="select"
                title=""
                placeholder="Document Type"
                options={documentOptions}
                value={row.documentType}
                onChange={(value) =>
                  handleFieldChange(index, "documentType", value)
                }
                error={getAttachmentError(index, "documentType")}
                showButton
                showSearch
              />
            </div>
            <div className="min-w-[100px]">
              <FormFieldSet
                type="select"
                placeholder="Active"
                title=""
                value={row.active}
                onChange={(value) => handleFieldChange(index, "active", value)}
                error={getAttachmentError(index, "active")}
                options={RecordStatus}
              />
            </div>
            <div className="flex-1">
              <FormFieldSet
                type="file"
                title=""
                placeholder="Choose File"
                value={row.chooseFiles}
                onChange={(value) =>
                  handleFieldChange(index, "chooseFiles", value)
                }
                error={getAttachmentError(index, "chooseFiles")}
              />
            </div>
            <div className="flex gap-2 mt-[30px]">
              <Button
                type="icon"
                size="xs"
                icon="add"
                color="black"
                onClick={handleAddRow}
              />
              <Button
                type="icon"
                size="xs"
                icon="remove"
                color="danger"
                onClick={() => handleRemoveRow(row.id)}
                disabled={formData.documentRows.length === 1}
              />
            </div>
          </div>
        ))} */}
        <AttachmentSection
          attachments={formData.attachments}
          onChange={(newAttachments) =>
            setFormData({ ...formData, attachments: newAttachments })
          }
          getAttachmentError={getAttachmentError}
          showNameField={true}
          showCommentsField={true}
          showDocumentStatusField={true}
          showDocumentTypeField={true}
        />
        <div className="flex justify-end gap-4 mt-9">
          <Button title={"Cancel"} variant="outlined" />
          <Button title={"Save"} onClick={handleSave} />
        </div>
        {fetchPerticularData && (
          <div
            className=" flex rounded-md items-center cursour-pointer"
            onClick={() => setAttachmentModal(!attachmentModal)}
          >
            <Chip
              label={`Attachments:-${combineAttachments.length}`}
              icon={"docs"}
              iconPosition={"before"}
            />
          </div>
        )}
      </div>
      <Modal
        isOpen={attachmentModal}
        onClose={() => setAttachmentModal(false)}
        // title="Attachment View"
        // width="max-w-5xl"
        width="max-w-2xl"
        title={"Document Details"}
      >
        {/* <div className="flex flex-col justify-center items-center gap-8">
              <h6>attachement Details</h6> */}
        <DocumentsList data={combineAttachments} />
        {/* <Button
                title={"Close"}
                onClick={() => setAttachmentModal(false)}
              /> */}
        {/* </div> */}
      </Modal>
    </div>
  );
};

export default InvoiceDetails;
